/**
 * HistogramChart — frequency distribution of a numeric series.
 *
 * Used in QC dashboards to show, e.g., the distribution of pour
 * temperatures across the last 500 heats — useful for spotting bi-modal
 * behaviour and process-drift symptoms.
 */

import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import type { WidgetState } from "../types";
import { SERIES_PALETTE } from "../series-palette";
import { inBand } from "./band";

export interface HistogramChartProps {
  values: number[];
  /** Number of bins. Default 16. */
  bins?: number;
  /** Optional inclusive target band — drawn as a tinted reference area. */
  targetMin?: number;
  targetMax?: number;
  xLabel?: string;
  unit?: string;
  /**
   * Fixed lower bound for the VALUE (binned, X) axis. The bins span
   * [yMin, yMax] so a fixed range behaves like the line charts ("range" =
   * the value window, e.g. moisture 0–100%). Omit for autorange (bins fit
   * the data's own min/max). The frequency/count (Y) axis always autoranges.
   */
  yMin?: number;
  /** Fixed upper bound for the value (binned, X) axis. Omit for autorange. */
  yMax?: number;
  height?: number;
  state?: WidgetState;
  className?: string;
}

/** A bin outside the target band. */
const OUTSIDE_FILL = "rgb(var(--color-on-surface-muted) / 0.35)";

/**
 * The decimals a histogram's values are written to: two significant digits of the bin width, so neighbouring bins never
 * read the same ("3.41 3.44 3.47", not "3 3 3"). Whole numbers for a bin a unit or more wide; at most 6. Pure.
 */
export function binDecimals(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 1;
  return Math.min(6, Math.max(0, 1 - Math.floor(Math.log10(width))));
}

export function HistogramChart({
  values,
  bins = 16,
  targetMin,
  targetMax,
  xLabel,
  unit,
  yMin,
  yMax,
  height = 240,
  state = { kind: "ok" },
  className,
}: HistogramChartProps) {
  const data = useMemo(() => {
    if (values.length === 0) return [];
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    // The fixed Axis range (yMin/yMax) bounds the VALUE (binned) axis so the
    // histogram is consistent with the line charts. Omitted bounds fall back
    // to the data's own min/max. Values outside the fixed window are dropped.
    const lo = typeof yMin === "number" && Number.isFinite(yMin) ? yMin : dataMin;
    const hi = typeof yMax === "number" && Number.isFinite(yMax) ? yMax : dataMax;
    if (hi <= lo) return [{ label: lo.toFixed(1), count: values.length, mid: lo }];
    const width = (hi - lo) / bins;
    const dp = binDecimals(width);
    const counts = Array.from({ length: bins }, (_, i) => ({
      label: `${(lo + i * width).toFixed(dp)} to ${(lo + (i + 1) * width).toFixed(dp)}`,
      mid: lo + (i + 0.5) * width,
      count: 0,
      inTarget: inBand(lo + (i + 0.5) * width, targetMin, targetMax),
    }));
    for (const v of values) {
      if (v < lo || v > hi) continue;
      const i = Math.min(bins - 1, Math.max(0, Math.floor((v - lo) / width)));
      counts[i].count++;
    }
    return counts;
  }, [values, bins, targetMin, targetMax, yMin, yMax]);
  // The value axis is written to the bins' own step, so its ticks never repeat.
  const tickDecimals = useMemo(() => {
    if (data.length < 2) return 1;
    return binDecimals(data[1].mid - data[0].mid);
  }, [data]);

  if (state.kind === "loading") {
    return <ChartSkeleton shape="histogram" height={height} className={className} />;
  }
  if (state.kind === "empty" || values.length === 0) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center rounded-md border border-dashed border-border bg-surface-elevated/40 text-sm text-on-surface-muted",
          className,
        )}
        style={{ height }}
      >
        No samples.
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
          <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="mid"
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 10 }}
            tickFormatter={(v: number) => v.toFixed(tickDecimals)}
            label={
              xLabel
                ? {
                    value: unit ? `${xLabel} (${unit})` : xLabel,
                    position: "insideBottom",
                    offset: -8,
                    style: { fontSize: 11, fill: "rgb(var(--color-on-surface-muted))" },
                  }
                : undefined
            }
          />
          <YAxis
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            domain={[0, "auto"]}
            allowDecimals={false}
            label={{
              value: "Readings",
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: "middle", fontSize: 11, fill: "rgb(var(--color-on-surface-muted))" },
            }}
          />
          {targetMin !== undefined && (
            <ReferenceLine x={targetMin} stroke="rgb(var(--color-success))" strokeDasharray="3 3" />
          )}
          {targetMax !== undefined && (
            <ReferenceLine x={targetMax} stroke="rgb(var(--color-success))" strokeDasharray="3 3" />
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: "rgb(var(--color-surface-elevated))",
              border: "1px solid rgb(var(--color-border))",
              borderRadius: 6,
              fontSize: 12,
            }}
            // The bin's range, written to its step, not the raw middle of the bin.
            labelFormatter={(_, payload) => {
              const bin = payload?.[0]?.payload as { label?: string } | undefined;
              return bin?.label ? (unit ? `${bin.label} ${unit}` : bin.label) : "";
            }}
          />
          <Bar dataKey="count" name="Readings" fill={SERIES_PALETTE[0]} radius={[3, 3, 0, 0]}>
            {/* A bin outside the target band is muted, so the band reads at a glance (B6). With no band, none is. */}
            {data.map((d, i) => (
              <Cell key={i} fill={"inTarget" in d && d.inTarget === false ? OUTSIDE_FILL : SERIES_PALETTE[0]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
